import type {
  ApiDocument,
  ApiFolder,
  MoveDocumentRequest,
  MoveDocumentResponse,
} from "@workith/shared";
import { canManageForRole, getSharedDriveCapabilities } from "@workith/shared";
import {
  Button,
  cn,
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@workith/ui";
import { Folder } from "lucide-react";
import { useEffect, useMemo, useState } from "react";
import api from "../../lib/api";

export interface MoveDocumentTarget {
  folder: ApiFolder;
  label: string;
  selectable: boolean;
  reason: string | null;
}

interface MoveDocumentDialogProps {
  document: ApiDocument;
  folders: ApiFolder[];
  open: boolean;
  onOpenChange: (open: boolean) => void;
  onMoved: () => void;
}

function documentScope(document: ApiDocument): "personal" | "shared_drive" {
  return document.folder_scope === "shared_drive" ? "shared_drive" : "personal";
}

function isMoveScopeCandidate(document: ApiDocument, folder: ApiFolder): boolean {
  if (documentScope(document) === "shared_drive") {
    return folder.scope === "shared_drive" && folder.shared_drive_id === document.shared_drive_id;
  }

  return folder.scope === "personal" || folder.scope === "shared_drive";
}

function canMoveFromDocument(document: ApiDocument): boolean {
  if (documentScope(document) === "shared_drive") {
    return getSharedDriveCapabilities(document.user_role).canWrite;
  }

  return canManageForRole(document.user_role);
}

function getFolderLabel(folder: ApiFolder, foldersById: Map<string, ApiFolder>): string {
  if (folder.root_kind === "personal_root") {
    return "개인 폴더";
  }

  const names: string[] = [];
  let current: ApiFolder | undefined = folder;

  while (current) {
    if (current.root_kind === "personal_root") {
      names.unshift("개인 폴더");
      break;
    }

    names.unshift(current.name);
    current = current.parent_id ? foldersById.get(current.parent_id) : undefined;
  }

  return names.join(" / ");
}

function getDisabledReason(document: ApiDocument, folder: ApiFolder): string | null {
  if (document.folder_id === folder.id) {
    return "현재 위치";
  }

  if (!canMoveFromDocument(document)) {
    return "문서 이동 권한 없음";
  }

  if (folder.scope === "shared_drive") {
    return getSharedDriveCapabilities(folder.user_role).canWrite
      ? null
      : "공유 드라이브 편집 권한 필요";
  }

  return null;
}

export function buildMoveTargets(
  document: ApiDocument,
  folders: ApiFolder[]
): MoveDocumentTarget[] {
  const foldersById = new Map(folders.map((folder) => [folder.id, folder]));

  return folders
    .filter((folder) => isMoveScopeCandidate(document, folder))
    .map((folder) => {
      const reason = getDisabledReason(document, folder);

      return {
        folder,
        label: getFolderLabel(folder, foldersById),
        selectable: reason === null,
        reason,
      };
    });
}

function reportMoveError(error: unknown) {
  console.error("Failed to move document", error);
  alert(error instanceof Error ? error.message : "Failed to move document");
}

export function MoveDocumentDialog({
  document,
  folders,
  open,
  onOpenChange,
  onMoved,
}: MoveDocumentDialogProps) {
  const [selectedFolderId, setSelectedFolderId] = useState<string | null>(null);
  const [saving, setSaving] = useState(false);
  const targets = useMemo(() => buildMoveTargets(document, folders), [document, folders]);
  const selectedTarget = targets.find((target) => target.folder.id === selectedFolderId);

  useEffect(() => {
    if (open) {
      setSelectedFolderId(null);
    }
  }, [open]);

  const submit = async () => {
    if (!selectedTarget?.selectable) {
      return;
    }

    setSaving(true);
    try {
      const request: MoveDocumentRequest = { folderId: selectedTarget.folder.id };
      await api.put<MoveDocumentRequest, MoveDocumentResponse>(
        `/api/documents/${document.id}/move`,
        request
      );
      onOpenChange(false);
      onMoved();
    } catch (error) {
      reportMoveError(error);
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-xl" aria-describedby={undefined}>
        <DialogHeader>
          <DialogTitle>문서 이동</DialogTitle>
        </DialogHeader>

        <div className="max-h-80 space-y-2 overflow-y-auto">
          {targets.map((target) => {
            const selected = target.folder.id === selectedFolderId;

            return (
              <button
                key={target.folder.id}
                type="button"
                disabled={!target.selectable}
                onClick={() => setSelectedFolderId(target.folder.id)}
                className={cn(
                  "flex w-full items-center justify-between rounded-md border p-3 text-left text-sm",
                  selected && "border-primary bg-accent",
                  target.selectable ? "hover:bg-accent" : "cursor-not-allowed opacity-50"
                )}
              >
                <span className="flex min-w-0 items-center gap-2">
                  <Folder className="h-4 w-4 shrink-0 text-muted-foreground" />
                  <span className="truncate">{target.label}</span>
                </span>
                {target.reason && <span className="ml-3 shrink-0 text-xs">{target.reason}</span>}
              </button>
            );
          })}
        </div>

        <DialogFooter>
          <Button type="button" variant="outline" onClick={() => onOpenChange(false)}>
            취소
          </Button>
          <Button type="button" onClick={submit} disabled={!selectedTarget?.selectable || saving}>
            이동
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
